import { Conversions, PublicKey as CLPublicKey } from 'casper-js-sdk';

// PublicKey casper nomenclature:
// - public key = base16 hex => algo prefix + public key hex
// - account hash - internal representation of a public key with a fixed length

// base16 hex: '01deba7173738a7f55de3ad9dc27e081df41ff285f25887ec424a8a65b43d0cf77'
// base64 "2qdt4zi/b/uagi2L9Y+db0I0Jt62CTpR/Td9HhiRAu0="

// ED = 01 public keys should be 66 chars long (with the prefix)
// SEC = 02 public keys should be 68 chars long (with the prefix)

type Input = { base64: string } | { publicKeyHex: string };

export interface AccountRawData {
  Account: AccountResult | null;
}

export interface AccountResult {
  account_hash: string;
  main_purse: string;
  named_keys: { key: string; name: string }[];
  associated_keys: { account_hash: string; weight: number }[];
  action_thresholds: {
    deployment: number;
    key_management: number;
  };
}

export function Account(obj: any): AccountResult {
  return obj;
}

export const AccountModel = (input: Input) => {
  const getRawPublicKey = () => {
    let value: CLPublicKey;

    if ('publicKeyHex' in input) {
      value = CLPublicKey.fromHex(input.publicKeyHex);
    } else if ('base64' in input) {
      // TODO: Seems like we are not using 'base64' input
      // TODO: base64 signer account will always use fromEd25519 because there is no prefix available
      // const bytes = Conversions.decodeBase64(input.base64);
      // value = CLPublicKey.fromEd25519(bytes);
    } else {
      throw Error('missing account key');
    }

    // @ts-ignore
    return value;
  };

  const getPublicKey = () => {
    // toAccountHex => sig key prefix + base16 (hex) hash
    return getRawPublicKey().toHex();
  };

  const getBase16AccountHash = () => {
    // toAccountHash => raw hash
    return Conversions.encodeBase16(getRawPublicKey().accountHash().toBytes());
  };

  const getBase64AccountHash = () => {
    // toAccountHash => raw hash
    return Conversions.encodeBase64(getRawPublicKey().accountHash().toBytes());
  };

  return {
    getPublicKey: getPublicKey,
    getAccountHash: getBase16AccountHash,
    getBase64AccountHash: getBase64AccountHash,
  };
};

export const getMainPurseUrefFromAccountRawData = (
  accountRawData: AccountRawData | null
): string | null =>
  accountRawData?.Account && accountRawData?.Account?.main_purse
    ? accountRawData?.Account?.main_purse
    : null;

export const getAccountHashFromPublicKey = (
  publicKey: string
): string | null => {
  if (publicKey === '') return null;

  return AccountModel({
    publicKeyHex: publicKey,
  }).getAccountHash();
};

export const getAssociatedKeysFromAccountRawData = (
  accountRawData: AccountRawData | null
): any | null =>
  accountRawData?.Account && accountRawData?.Account?.associated_keys
    ? accountRawData?.Account?.associated_keys
    : null;
